import React, { useContext, useEffect, useMemo, useState } from 'react'
import PageHeader from '../../../components/PageHeader/PageHeader'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {
  Archive,
  AssignmentTurnedIn,
  DashboardCustomize,
  DoorSliding,
  FactCheck,
  Inventory2,
  LocalPharmacy,
  LockOpen,
  MedicalServices,
  MenuBook,
  OpenInNew,
  PriorityHigh,
  ReportProblem,
  Search,
  Shield,
  WarningAmber,
} from '@mui/icons-material'
import { Link, useNavigate } from 'react-router-dom'
import {
  Avatar,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { keyframes } from '@mui/material/styles'
import AmbulanceIcon from '../../../assets/icons/Ambulance'

// Gentle red background pulse for bags holding out-of-date medicines
const expiredPulse = keyframes`
  0%, 100% { background-color: #FFFFFF; }
  50% { background-color: #FEE2E2; }
`

// The "Expired" chip pulses in counter-phase: strongest while the card is
// at rest, lightest while the card is at its reddest. Same duration so the
// two stay locked together.
const expiredChipPulse = keyframes`
  0%, 100% { background-color: #DC2626; }
  50% { background-color: #F87171; }
`

const defaultLevels = [
  { name: 'General', color: '#242F40' },
  { name: 'Intermediate', color: '#85276A' },
  { name: 'Advanced', color: '#E32525' },
  { name: 'Specialist', color: '#1F3D7C' },
  { name: 'Other', color: '#239AA4' },
]

const defaultLevelColorMap = defaultLevels.reduce(
  (acc, level) => ({
    ...acc,
    [level.name.toLowerCase()]: level.color,
  }),
  {},
)

const getLevel = (levels, value) => {
  if (value === undefined || value === null || value === '') return null

  if (Number.isInteger(Number(value)) && levels[Number(value)]) {
    return levels[Number(value)]
  }

  return (
    levels.find(
      (level) =>
        level.name?.toLowerCase?.() === String(value).trim().toLowerCase(),
    ) || { name: String(value), color: '#64748B' }
  )
}

const getLocationColor = (location, levels) => {
  if (location.archived) return '#64748B'
  if (location.type === 'Medicine Store') return '#1E7F88'

  return getLevel(levels, location.level)?.color || '#242F40'
}

const assignedLabel = (location) => {
  if (location?.assignedToVehicle) {
    return location.assignedToVehicle.callSign
  }

  if (location?.assignedToEmployee) {
    return [
      location.assignedToEmployee.userAccount?.firstName,
      location.assignedToEmployee.userAccount?.lastName,
    ]
      .filter(Boolean)
      .join(' ')
  }

  if (location?.assignedToEvent) {
    return location.assignedToEvent.title
  }

  if (location?.assignedToStore) {
    return location.assignedToStore.name
  }

  return 'Unassigned'
}

const SummaryTile = ({ icon: Icon, label, value, tone, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.25,
      borderRadius: 2,
      border: '1px solid rgba(15,23,42,0.08)',
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: '#fff',
      transition: 'border-color 0.18s ease, transform 0.18s ease',
      '&:hover': onClick
        ? { borderColor: tone, transform: 'translateY(-1px)' }
        : undefined,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          color: tone,
          backgroundColor: `${tone}18`,
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>

      <Stack minWidth={0}>
        <Typography fontWeight={950} fontSize="1.25rem" lineHeight={1}>
          {value ?? 0}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
    </Stack>
  </Paper>
)

const KeyItem = ({ text, color }) => (
  <Chip
    size="small"
    label={text}
    sx={{
      borderRadius: 1,
      fontWeight: 800,
      border: `1px solid ${color}33`,
      color,
      backgroundColor: `${color}12`,
      '&::before': {
        content: '""',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        ml: 1,
      },
      gap: 0.75,
    }}
  />
)

const LocationTile = ({ location, icon, levels }) => {
  const hasExpired = !!location?.firstExpired
  const expiringSoon = !!location?.hasMedsExpiringThisMonth
  // Stores that allow out-of-date stock (quarantine) never raise the red
  // flag - holding expired items is their job - but the tile still says so,
  // as information rather than an alarm.
  const heldExpired = location?.allowExpiredMedicines
    ? location?.expiredHeldCount || 0
    : 0
  const color = getLocationColor(location, levels)
  const level = getLevel(levels, location.level)
  const isBag = location.type === 'Medicine Bag'
  const sealRemoved = isBag && location.tagNumber === 'Removed'
  const archived = !!location.archived
  const assignee = assignedLabel(location)
  const pulse = isBag && hasExpired && !archived

  return (
    <Link
      to={location?._id}
      style={{
        display: 'block',
        height: '100%',
        textDecoration: 'none',
      }}
    >
        <Paper
          elevation={0}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 0,
            // Padding + borders inside the 100% width - with content-box the
            // tile was 13px wider than its grid cell and overlapped the next
            boxSizing: 'border-box',
            minHeight: 78,
            p: 1.25,
            borderRadius: 2,
            border: '1px solid rgba(15,23,42,0.08)',
            borderLeft: `5px solid ${color}`,
            backgroundColor: archived ? '#F1F5F9' : '#fff',
            ...(pulse && {
              // Not the left edge - that stripe is the bag level colour
              borderTopColor: 'rgba(220,38,38,0.35)',
              borderRightColor: 'rgba(220,38,38,0.35)',
              borderBottomColor: 'rgba(220,38,38,0.35)',
              animation: `${expiredPulse} 2.4s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                backgroundColor: '#FEF2F2',
              },
            }),
            color: 'text.primary',
            transition: 'border-color 0.18s ease, transform 0.18s ease',
            '&:hover': {
              borderColor: `${color}88`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Stack spacing={1} height="100%" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  color,
                  backgroundColor: `${color}14`,
                  flexShrink: 0,
                }}
              >
                {location?.assignedToEmployee?.id ? (
                  <Tooltip title={`Assigned to ${assignee}`}>
                    <Avatar
                      src={location.assignedToEmployee?.userAccount?.photograph}
                      sx={{ borderRadius: 1, width: 24, height: 24 }}
                    >
                      {location.assignedToEmployee?.userAccount?.firstName?.[0]}
                      {location.assignedToEmployee?.userAccount?.lastName?.[0]}
                    </Avatar>
                  </Tooltip>
                ) : location?.assignedToVehicle ? (
                  <AmbulanceIcon />
                ) : (
                  icon
                )}
              </Box>

              <Stack minWidth={0} flex={1} spacing={0.25}>
                <Typography fontWeight={900} lineHeight={1.15} noWrap>
                  {location.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {isBag
                    ? assignee
                    : [
                        location.allowAdministration
                          ? 'Mobile store'
                          : 'Medicine store',
                        location.allowExpiredMedicines &&
                          'out of date stock allowed',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {isBag && level?.name && (
                <Chip
                  size="small"
                  label={level.name}
                  sx={{
                    borderRadius: 1,
                    height: 22,
                    fontWeight: 800,
                    color,
                    backgroundColor: `${color}14`,
                  }}
                />
              )}
              {hasExpired && !archived && (
                <Tooltip title="Has out of date medicines">
                  <Chip
                    size="small"
                    label="Expired"
                    sx={{
                      borderRadius: 1,
                      height: 22,
                      fontWeight: 800,
                      color: '#fff',
                      backgroundColor: '#DC2626',
                      ...(pulse && {
                        animation: `${expiredChipPulse} 2.4s ease-in-out infinite`,
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                        },
                      }),
                    }}
                  />
                </Tooltip>
              )}
              {!hasExpired && expiringSoon && !archived && (
                <Tooltip title="Medicines expiring within 30 days">
                  <Chip
                    size="small"
                    label="Expiring"
                    sx={{
                      borderRadius: 1,
                      height: 22,
                      fontWeight: 800,
                      color: '#B45309',
                      backgroundColor: '#FEF3C7',
                    }}
                  />
                </Tooltip>
              )}
              {heldExpired > 0 && !archived && (
                <Tooltip
                  title={`Holding ${heldExpired} out of date item${
                    heldExpired === 1 ? '' : 's'
                  } awaiting disposal`}
                >
                  <Chip
                    size="small"
                    label={`${heldExpired} to dispose`}
                    sx={{ borderRadius: 1, height: 22, fontWeight: 800 }}
                  />
                </Tooltip>
              )}
              {sealRemoved && (
                <Tooltip title="Seal removed">
                  <Chip
                    size="small"
                    icon={<LockOpen />}
                    aria-label="Seal removed"
                    color="info"
                    sx={{
                      borderRadius: 1,
                      height: 22,
                      '& .MuiChip-icon': { mx: 0.5 },
                      '& .MuiChip-label': { display: 'none' },
                    }}
                  />
                </Tooltip>
              )}
              {archived && (
                <Chip
                  size="small"
                  label="Archived"
                  sx={{ borderRadius: 1, height: 22, fontWeight: 800 }}
                />
              )}
            </Stack>
          </Stack>
        </Paper>
    </Link>
  )
}

export const LocationRow = ({ header, locations, icon, levels }) => {
  if (locations.length === 0) return null

  const sortedLocations = [...locations].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || ''),
  )

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px solid rgba(15,23,42,0.08)',
        backgroundColor: '#fff',
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography fontWeight={950}>{header}</Typography>
          <Chip
            size="small"
            label={locations.length}
            sx={{ borderRadius: 1, fontWeight: 800 }}
          />
        </Stack>
        <Box>
          <Grid container spacing={2}>
            {sortedLocations.map((location) => (
              <Grid item xs={12} sm={6} lg={4} xl={3} key={location._id}>
                <LocationTile location={location} icon={icon} levels={levels} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Paper>
  )
}

const MedsMxIndex = () => {
  const [loading, setLoading] = useState(true)
  const [stores, setStores] = useState([])
  const [groupedBags, setGroupedBags] = useState({})
  const [showArchived, setShowArchived] = useState(false)
  const [viewMode, setViewMode] = useState('Assignment')
  const [levels, setLevels] = useState(defaultLevels)
  const [expiredCount, setExpiredCount] = useState(0)
  const [expiringMonthCount, setExpiringMonthCount] = useState(0)
  const [expiring3MonthCount, setExpiring3MonthCount] = useState(0)
  // null = no access (governance-only endpoints) - the buttons stay hidden
  const [openDiscrepancies, setOpenDiscrepancies] = useState(null)
  const [hasControlledDrugs, setHasControlledDrugs] = useState(false)

  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { baseLocationOptions = [] } = useContext(GlobalContext)

  // The lists are scoped server-side to the bases selected in the header
  // (within what the user may see). Say so, unless a site admin is looking
  // at everything anyway.
  const scopeCaption = useMemo(() => {
    const selectedIds = (user?.selectedBaseLocations || []).map(String)
    const inScope = baseLocationOptions.filter(
      (base) =>
        selectedIds.length === 0 || selectedIds.includes(String(base._id)),
    )
    const isSiteAdmin = !!user?.security?.isSiteAdmin
    if (isSiteAdmin && inScope.length === baseLocationOptions.length) {
      return ''
    }
    if (inScope.length === 0) return 'No base locations selected'
    return `Showing stores and bags at ${inScope.map((b) => b.name).join(', ')}`
  }, [user, baseLocationOptions])

  useEffect(() => {
    API.get('/medsmx/meds-headers').then((res) => {
      if (res.status === 200) {
        setExpiredCount(res.data.totalExpiredMeds)
        setExpiringMonthCount(res.data.expiringThisMonth)
        setExpiring3MonthCount(res.data.expiring3Months)
      }
    })
    // Governance-only extras; a 403 just leaves the buttons hidden.
    API.get('/medsmx/discrepancies').then((res) => {
      if (res.status === 200) {
        setOpenDiscrepancies(
          res.data.filter((row) => row.status !== 'Resolved').length,
        )
      }
    })
    API.get('/medsmx/controlleddruglist').then((res) => {
      if (res.status === 200) setHasControlledDrugs(res.data.length > 0)
    })
  }, [])

  const normaliseBagLevel = (level) => {
    if (typeof level === 'string') {
      const trimmed = level.trim()
      return {
        name: trimmed,
        color: defaultLevelColorMap[trimmed.toLowerCase()] || '#64748B',
      }
    }

    if (level?.name) {
      const trimmed = level.name.trim()
      return {
        name: trimmed,
        color:
          level.color ||
          defaultLevelColorMap[trimmed.toLowerCase()] ||
          '#64748B',
      }
    }

    if (typeof level === 'object' && level !== null) {
      const [name, color] = Object.entries(level)[0] || []
      const trimmed = name?.trim?.() || ''
      return {
        name: trimmed,
        color:
          color || defaultLevelColorMap[trimmed.toLowerCase()] || '#64748B',
      }
    }

    return { name: '', color: '#64748B' }
  }

  useEffect(() => {
    API.get('/settings?select=medsmanagement.bagLevels').then((res) => {
      const data = res?.data?.medsmanagement?.bagLevels

      if (res.status === 200 && Array.isArray(data) && data.length) {
        const normalised = data.map(normaliseBagLevel).filter((l) => l.name)

        setLevels(normalised)
      }
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    API.get(`/siteConfig/medicines/locations?showArchived=${showArchived}`)
      .then((res) => {
        const locations = Array.isArray(res.data) ? res.data : []
        const nextStores = locations
          .filter((location) => location.type === 'Medicine Store')
          .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        const grouped = {}

        locations
          .filter((location) => location.type === 'Medicine Bag')
          .sort((a, b) => Number(a.number || 0) - Number(b.number || 0))
          .forEach((bag) => {
            let groupKey

            if (viewMode === 'Assignment') {
              groupKey = assignedLabel(bag)
              if (bag.assignedToStore) groupKey = `Store ${groupKey}`
              if (bag.assignedToEvent) groupKey = `Event ${groupKey}`
            } else {
              groupKey = getLevel(levels, bag.level)?.name || 'Uncategorised'
            }

            if (!grouped[groupKey]) grouped[groupKey] = []
            grouped[groupKey].push(bag)
          })

        setStores(nextStores)
        setGroupedBags(grouped)
      })
      .finally(() => setLoading(false))
  }, [levels, showArchived, viewMode])

  const bagGroups = useMemo(
    () =>
      Object.entries(groupedBags).sort(([a], [b]) => {
        if (a === 'Unassigned') return -1
        if (b === 'Unassigned') return 1
        return a.localeCompare(b)
      }),
    [groupedBags],
  )

  const bagCount = useMemo(
    () =>
      bagGroups.reduce((total, [, locations]) => total + locations.length, 0),
    [bagGroups],
  )
  const unassignedCount = groupedBags.Unassigned?.length || 0
  const removedSealCount = useMemo(
    () =>
      bagGroups.reduce(
        (total, [, locations]) =>
          total +
          locations.filter((location) => location.tagNumber === 'Removed')
            .length,
        0,
      ),
    [bagGroups],
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ pb: 3, maxWidth: 1680, mx: 'auto', width: '100%' }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
      >
        <Stack spacing={0.5}>
          <PageHeader title="Medicines Management" />
          <Typography variant="body2" color="text.secondary">
            Stores, bags, audits, expiry risk, and administration records.
          </Typography>
          {scopeCaption && (
            <Typography variant="caption" color="text.secondary">
              {scopeCaption} · change the base locations in the header to see
              others
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            startIcon={<Search />}
            endIcon={<OpenInNew />}
            onClick={() => navigate('/medicines-management/viewall')}
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
          >
            Medicines
          </Button>
          <Button
            startIcon={<AssignmentTurnedIn />}
            onClick={() =>
              navigate('/medicines-management/administration-records')
            }
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
          >
            Administration
          </Button>
          {hasControlledDrugs && (
            <Button
              startIcon={<MenuBook />}
              onClick={() => navigate('/medicines-management/cd-register')}
              variant="outlined"
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
            >
              CD Register
            </Button>
          )}
          {openDiscrepancies !== null && (
            <Button
              startIcon={<ReportProblem />}
              onClick={() => navigate('/medicines-management/discrepancies')}
              variant="outlined"
              color={openDiscrepancies > 0 ? 'error' : 'primary'}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
            >
              {openDiscrepancies > 0
                ? `Discrepancies (${openDiscrepancies})`
                : 'Discrepancies'}
            </Button>
          )}
          <Button
            startIcon={<FactCheck />}
            onClick={() => navigate('/medicines-management/audits')}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#1E7F88',
            }}
          >
            Audits
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(7, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        <SummaryTile
          icon={WarningAmber}
          label="Expired"
          value={expiredCount}
          tone={expiredCount > 0 ? '#DC2626' : '#16A34A'}
          onClick={() => navigate('/medicines-management/viewall?expiry=today')}
        />
        <SummaryTile
          icon={PriorityHigh}
          label="30 days"
          value={expiringMonthCount}
          tone={expiringMonthCount > 0 ? '#D97706' : '#16A34A'}
          onClick={() =>
            navigate('/medicines-management/viewall?expiry=30days')
          }
        />
        <SummaryTile
          icon={Inventory2}
          label="Bags"
          value={bagCount}
          tone="#2563EB"
        />
        <SummaryTile
          icon={Shield}
          label="Seal removed"
          value={removedSealCount}
          tone={removedSealCount > 0 ? '#D97706' : '#16A34A'}
        />
        <SummaryTile
          icon={DoorSliding}
          label="Stores"
          value={stores.length}
          tone="#1E7F88"
        />
        <SummaryTile
          icon={MedicalServices}
          label="Unassigned"
          value={unassignedCount}
          tone={unassignedCount > 0 ? '#D97706' : '#16A34A'}
        />
        <SummaryTile
          icon={Inventory2}
          label="3 months"
          value={expiring3MonthCount}
          tone={expiring3MonthCount > 0 ? '#D97706' : '#16A34A'}
          onClick={() =>
            navigate('/medicines-management/viewall?expiry=3months')
          }
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: '1px solid rgba(15,23,42,0.08)',
          backgroundColor: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={(_, value) => value && setViewMode(value)}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  textTransform: 'none',
                  fontWeight: 850,
                },
              }}
            >
              <ToggleButton value="Assignment">
                <DashboardCustomize fontSize="small" sx={{ mr: 0.75 }} />
                Assignment
              </ToggleButton>
              <ToggleButton value="Bag Type">
                <LocalPharmacy fontSize="small" sx={{ mr: 0.75 }} />
                Bag type
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              startIcon={<Archive />}
              onClick={() => setShowArchived((prev) => !prev)}
              variant={showArchived ? 'contained' : 'outlined'}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 850 }}
            >
              {showArchived ? 'Hide archived' : 'Show archived'}
            </Button>
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {levels.map((level) => (
              <KeyItem text={level.name} color={level.color} key={level.name} />
            ))}
          </Stack>
        </Stack>

        {loading && <LinearProgress sx={{ mt: 1.5 }} />}
      </Paper>

      <LocationRow
        header="Medicine Stores"
        locations={stores}
        icon={<DoorSliding />}
        levels={levels}
      />

      {!loading && stores.length === 0 && bagCount === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            border: '1px solid rgba(15,23,42,0.08)',
            backgroundColor: '#fff',
          }}
        >
          <Stack spacing={2} maxWidth={720}>
            <Stack spacing={0.5}>
              <Typography fontWeight={950} fontSize="1.15rem">
                Set up medicines management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nothing here yet — three steps will get your stores and bags up
                and running.
              </Typography>
            </Stack>

            {[
              {
                icon: LocalPharmacy,
                title: 'Build your formulary',
                body: 'Add the medicines your organisation carries, in Site Configuration → Governance → Medicines.',
              },
              {
                icon: DoorSliding,
                title: 'Create a store and your bags',
                body: 'On the same screen, add a medicine store (bulk stock) and the bags your crews take out.',
              },
              {
                icon: MedicalServices,
                title: 'Add stock, then assign it to bags',
                body: 'Open your store here, use "Add stock" to enter deliveries, then select a medicine to assign it to a bag.',
              },
            ].map((step, index) => (
              <Stack
                key={step.title}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#1E7F88',
                    backgroundColor: 'rgba(30,127,136,0.10)',
                    flexShrink: 0,
                  }}
                >
                  <step.icon fontSize="small" />
                </Box>
                <Stack spacing={0.25}>
                  <Typography fontWeight={850}>
                    {index + 1}. {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.body}
                  </Typography>
                </Stack>
              </Stack>
            ))}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => navigate('/site-configuration/medicines')}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#1E7F88',
                  width: 'fit-content',
                }}
              >
                Open medicine settings
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Medicine settings need site configuration access — if the button
              above doesn&apos;t work for you, ask an administrator to complete
              the setup.
            </Typography>
          </Stack>
        </Paper>
      ) : bagGroups.length === 0 && !loading ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: '1px dashed rgba(15,23,42,0.18)',
            backgroundColor: '#F8FAFC',
          }}
        >
          <Typography fontWeight={900}>No medicine bags yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Create bags in Site Configuration → Governance → Medicines, then
            assign stock to them from a store — they&apos;ll appear here.
          </Typography>
        </Paper>
      ) : (
        bagGroups.map(([group, locations]) => (
          <LocationRow
            key={group}
            header={group}
            locations={locations}
            icon={<MedicalServices />}
            levels={levels}
          />
        ))
      )}
    </Box>
  )
}

export default MedsMxIndex
